import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { addMinutes, addYears, format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { DateTimePicker } from "../ui/date-time-picker";
import { useScheduleArticle } from "../../hooks/useArticles";
import { parseInputToUTC, getBrowserTimezone } from "../../lib/dateUtils";

interface ScheduleArticleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleId: string;
  // The current schedule, to edit it
  scheduledPublishAt?: string | null;
}

// The picker works in "yyyy-MM-ddTHH:mm" local time
const toPickerValue = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

/** Sets a draft to publish itself at a time, in the browser's time zone (the scheduler checks every minute) */
export function ScheduleArticleDialog({ open, onOpenChange, articleId, scheduledPublishAt }: ScheduleArticleDialogProps) {
  const scheduleArticle = useScheduleArticle();
  const [value, setValue] = useState<string | undefined>(() =>
    toPickerValue(scheduledPublishAt ? new Date(scheduledPublishAt) : addMinutes(new Date(), 60))
  );

  const publishAt = value ? parseInputToUTC(value) : null;
  const when = publishAt ? new Date(publishAt) : null;
  const tooSoon = !!when && when <= addMinutes(new Date(), 1);
  const tooLate = !!when && when > addYears(new Date(), 1);
  const problem = !when ? "Choose a date and time" : tooSoon ? "Choose a time in the future" : tooLate ? "An article can be scheduled up to a year ahead" : null;

  const handleSchedule = async () => {
    if (!publishAt || problem) return;
    try {
      await scheduleArticle.mutateAsync({ articleId, publishAt });
      onOpenChange(false);
    } catch {
      // Shown by the mutation hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" aria-hidden="true" />
            Schedule publishing
          </DialogTitle>
          <DialogDescription>
            The article stays a draft until then, and is published automatically within a minute of the time you
            choose. You can keep editing it, publish it sooner or cancel the schedule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <DateTimePicker value={value} onChange={setValue} placeholder="Pick a date and time" />
          <p className="text-xs text-muted-foreground">Times are in your time zone ({getBrowserTimezone()}).</p>
          {problem && value && <p className="text-sm text-destructive">{problem}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSchedule} disabled={!!problem || scheduleArticle.isPending}>
            {scheduleArticle.isPending ? "Scheduling..." : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
